import { useCallback, useEffect, useRef, useState } from 'react';
import requests from '../api/client';
import type { PaymentStatusKind } from '../types/api';

/**
 * Polling de confirmação do PIX.
 *
 * É o coração da UX do produto: assim que o webhook da Safe2Pay marca o
 * pagamento na API, `data_pagamento` deixa de ser nulo e a tela confirma
 * sozinha, sem o cliente precisar recarregar nada.
 *
 * Cadência e limite idênticos ao legado: uma consulta a cada 5 s, no máximo
 * 120 tentativas (10 minutos).
 */

export const POLLING_INTERVAL_MS = 5000;
export const POLLING_MAX_ATTEMPTS = 120;

export type PixPollingStatus = 'idle' | 'polling' | 'paid' | 'timeout';

export function usePixPolling() {
  const [status, setStatus] = useState<PixPollingStatus>('idle');
  const intervalRef = useRef<number | null>(null);
  const attemptsRef = useRef(0);

  const stop = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stop();
    attemptsRef.current = 0;
    setStatus('idle');
  }, [stop]);

  /**
   * `tipo` escolhe a tabela consultada na API. O padrão `ATIVREC` serve o
   * Recarga Fácil, que é sempre recarga; o Fluxo A passa `LOGISTICA` quando a
   * solicitação é uma compra (`t=C`), cujo id vive em `OPERADORA_PEDIDO`.
   * Consultar a tabela errada acha outra solicitação com o mesmo id numérico.
   */
  const start = useCallback(
    (solicitacaoId: number | string, tipo: PaymentStatusKind = 'ATIVREC') => {
      stop();
      attemptsRef.current = 0;
      setStatus('polling');

      intervalRef.current = window.setInterval(async () => {
        attemptsRef.current += 1;

        if (attemptsRef.current > POLLING_MAX_ATTEMPTS) {
          stop();
          setStatus('timeout');
          return;
        }

        try {
          const result = await requests.paymentStatus(solicitacaoId, tipo);
          if (result?.data_pagamento) {
            stop();
            setStatus('paid');
          }
        } catch {
          // Falha de rede numa tentativa não encerra o polling: a próxima
          // tentativa acontece em 5 s, igual ao comportamento antigo.
        }
      }, POLLING_INTERVAL_MS);
    },
    [stop]
  );

  useEffect(() => stop, [stop]);

  return { status, start, stop, reset };
}
